import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { DashboardSkeleton } from '../../lib/skeletons';
import { Home, FileText, DollarSign, Clock, Package, Building2, Briefcase } from 'lucide-react';
import { DashboardGeneral } from './DashboardGeneral';
import { DashboardAdmin } from './DashboardAdmin';
import { DashboardContabilidad } from './DashboardContabilidad';
import { DashboardOperaciones } from './DashboardOperaciones';
import { DashboardCompras } from './DashboardCompras';
import { DashboardBodega } from './DashboardBodega';
import { DashboardGerencia } from './DashboardGerencia';
import { SyncCenterWidget } from '../../components/dashboard/SyncCenterWidget';

const TABS = [
  { key: 'inicio', label: 'Inicio', icon: Home, endpoint: '/dashboard/general', module: null },
  { key: 'admin', label: 'Administrador', icon: Building2, endpoint: '/dashboard/admin', module: null },
  { key: 'gerencia', label: 'Gerencia', icon: Briefcase, endpoint: '/dashboard/gerencia', module: null },
  { key: 'contabilidad', label: 'Contabilidad', icon: DollarSign, endpoint: '/dashboard/contabilidad', module: 'contabilidad' },
  { key: 'operaciones', label: 'Operaciones', icon: Clock, endpoint: '/dashboard/operaciones', module: 'operaciones' },
  { key: 'compras', label: 'Compras', icon: FileText, endpoint: '/dashboard/compras', module: 'compras' },
  { key: 'bodega', label: 'Bodega', icon: Package, endpoint: '/dashboard/bodega', module: 'bodega' },
];

const DashComponents = {
  inicio: DashboardGeneral,
  admin: DashboardAdmin,
  gerencia: DashboardGerencia,
  contabilidad: DashboardContabilidad,
  operaciones: DashboardOperaciones,
  compras: DashboardCompras,
  bodega: DashboardBodega,
};

export const Dashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(null);
  const [tabData, setTabData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // User data comes from AuthContext via httpOnly cookie session
  }, []);

  const visibleTabs = useMemo(() => {
    if (!user) return TABS.filter(t => t.key === 'inicio');
    const rol = user.rol || 'consulta';
    const permisos = user.permisos || {};

    return TABS.filter(tab => {
      // "Inicio" always visible
      if (tab.key === 'inicio') return true;
      // "Admin" only for admin role
      if (tab.key === 'admin') return rol === 'admin';
      // "Gerencia" for admin and gerencia roles
      if (tab.key === 'gerencia') return rol === 'admin' || rol === 'gerencia';
      // Module-based tabs: check permissions
      if (tab.module) {
        if (rol === 'admin') return true;
        return permisos[tab.module]?.ver === true;
      }
      return false;
    });
  }, [user]);

  useEffect(() => {
    if (visibleTabs.length > 0 && !activeTab) {
      setActiveTab(visibleTabs[0].key);
    }
  }, [visibleTabs, activeTab]);

  const [loadedTabs, setLoadedTabs] = useState(new Set());

  const loadTabData = useCallback(async (tabKey) => {
    const tab = TABS.find(t => t.key === tabKey);
    if (!tab) return;
    setLoading(true);
    try {
      const res = await api.get(tab.endpoint);
      setTabData(prev => ({ ...prev, [tabKey]: res.data }));
      setLoadedTabs(prev => new Set([...prev, tabKey]));
    } catch (err) { console.error('caught:', err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    // Siempre recargar al cambiar de tab (drop cache) para reflejar cambios recientes
    // de OCs/aprobaciones/recepciones después de navegar.
    if (activeTab) loadTabData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const ActiveDash = activeTab ? DashComponents[activeTab] : null;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">CHIRINO STEEL - Sistema de Gestion</p>
        </div>
      </div>

      {/* Sync Center: estado de todas las sincronizaciones (siempre visible) */}
      {user && (user.rol === 'admin' || user.rol === 'gerencia') && <SyncCenterWidget />}

      {visibleTabs.length > 1 && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto" data-testid="dashboard-tabs">
          {visibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`dash-tab-${tab.key}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>
      )}

      {loading && !tabData[activeTab] ? (
        <DashboardSkeleton cards={4} />
      ) : ActiveDash ? (
        <ActiveDash data={tabData[activeTab]} onRefresh={() => { setLoadedTabs(prev => { const n = new Set(prev); n.delete(activeTab); return n; }); loadTabData(activeTab); }} />
      ) : null}
    </div>
  );
};

export default Dashboard;
